import { BAR_SLOTS, EffectLimit, barOf, blowOf, type Ability, type Effect, type ResourceId, type Rules } from "odyssey-shared";
import type { GlyphName } from "../../ui/kit/Glyph.tsx";

/**
 * What the ability bar and the status rows draw, read from the rules (P2.2-T4, question 4; the design's `AbilitySlot`
 * and `StatusIcon` in `components/slots/ItemSlot.jsx`): ten slots on keys 1 to 9 and 0 (the author's ruling of
 * 2026-10-02, P2.2-T11), each holding the ability of the bar the map server holds at its place (`barOf`), or nothing;
 * each ability's and effect's glyph; whether an effect is a buff or a debuff; and whether a slot can be used now, from
 * what the server last said of the player.
 */

/**
 * The slots' names by place, in slot order: 1 to 9, then 0 for the tenth (the author's ruling of 2026-10-02, the
 * design's ten on 1 to 0). They are each slot's default key and the name a slot is found by (`data-hud`); the key a
 * slot answers and shows is the one the page's key table binds it to (`controls/`).
 */
export const ABILITY_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"] as const;

/**
 * How many slots the bar has: the shared `BAR_SLOTS` (ADR-121 decision 2.2), one per name in `ABILITY_KEYS`.
 */
export const BAR_SLOT_COUNT = BAR_SLOTS;

/**
 * The bar's geometry in logical pixels (the design's `AbilitySlot` and the export's `HUD_SLOTS.abilities`): a slot 22
 * square, slots 1 apart; inside a slot's 2 px edge an 18 px well the cooldown wipes down from the top, or, while the
 * slot holds colour art, the 20 inside its 1 px edge (ADR-111 decision 19); the icon 16 in its middle, the key at the
 * top left and the cost at the bottom right; the seconds on the wipe only while it is at least 10 tall.
 */
export const ABILITY_SLOT = { size: 22, gap: 1, edge: 2, well: 18, artWell: 20, icon: 16, secondsFrom: 10 } as const;

/**
 * The status rows' geometry in logical pixels (the design's `StatusIcon`): an 18 px box with a 12 px icon in a column
 * 24 wide, columns 2 apart, the time under the box 1 below it, and as many columns as the row's slot holds. An
 * effect's colour tile is scaled for box 16 (`artBox`) and drawn in the 14 inside the box's 1 px edge and its 1 px tone ring
 * (`artRoom`), so the ring that says buff or debuff stays whole (ADR-111 decisions 15 and 17).
 */
export const STATUS_ICON = { box: 18, icon: 12, column: 24, gap: 2, timeGap: 1, artBox: 16, artRoom: 14 } as const;

/**
 * Each ability's glyph by id (original art, `ui/art/marks/glyph/`); an ability without one draws its slot with no icon.
 */
export const ABILITY_GLYPHS: Readonly<Record<string, GlyphName>> = { strike: "bullseye", heavy_strike: "sword", evasion: "human-run" };

/**
 * Each effect's glyph by id (original art, `ui/art/marks/glyph/`); an effect without one draws its ring with no icon.
 */
export const EFFECT_GLYPHS: Readonly<Record<string, GlyphName>> = { stun: "zap-large", resurrection_sickness: "hourglass-large", poison: "venom", battle_fatigue: "hourglass-large", shattered_focus: "hourglass-large", safe_passage: "passage" };

/**
 * One slot as the rules give it: its place and key, the ability's number (0 for an empty slot), name, glyph and icon
 * key (null for none; an ability's names a tile, ADR-111 decision 3), its first cost, which the slot prints, its
 * costs, and whether it holds the starting weapon's basic blow, which is never withheld for cost (ADR-117 decision 9).
 * A slot holding an item (any slot of a bar the player arranged, ADR-121 decision 9; the last slot of the default bar
 * while the bag holds a consumable, ADR-091 decision 14) carries the item's rules id, its icon key and the number
 * carried across every stack, which it prints where another slot prints its cost; its ability is then the record the
 * item's use runs, and its name, glyph and icon the item's. `spent` marks an item slot at 0, which keeps its item greyed
 * (ADR-121 decision 1.2). Every other slot's `item` and `count` are null, and an empty slot's `icon` too.
 */
export interface BarSlotSpec {
  readonly index: number;
  readonly key: string;
  readonly ability: number;
  readonly name: string | null;
  readonly glyph: GlyphName | null;
  readonly cost: { readonly resource: ResourceId; readonly amount: number } | null;
  readonly costs: Ability["costs"];
  readonly basic: boolean;
  readonly item: string | null;
  readonly icon: string | null;
  readonly count: number | null;
  readonly spent: boolean;
}

/**
 * The ten slots: the bar's abilities in order (`barOf`), the rest empty.
 *
 * @param rules The page's rules.
 * @returns The slots, in slot order.
 * @example
 * barSlots(loadRules()).map((slot) => slot.name);
 * // ["Strike", "Heavy Strike", "Evasion", null]
 */
export function barSlots(rules: Rules): readonly BarSlotSpec[] {
  const bar = barOf(rules);
  return ABILITY_KEYS.map((_, index) => {
    const ability = bar[index];
    return ability === undefined ? emptySlot(index) : abilitySlot(rules, index, ability);
  });
}

/**
 * An empty slot at a place.
 *
 * @param index The slot, from 0.
 * @returns The slot.
 */
export function emptySlot(index: number): BarSlotSpec {
  return { index, key: ABILITY_KEYS[index] ?? "", ability: 0, name: null, glyph: null, cost: null, costs: [], basic: false, item: null, icon: null, count: null, spent: false };
}

/**
 * A slot holding an ability: its number, name, glyph, icon, first cost and costs, and whether it is the starting
 * weapon's basic blow.
 *
 * @param rules The page's rules.
 * @param index The slot, from 0.
 * @param ability The ability, one these rules hold.
 * @returns The slot.
 */
export function abilitySlot(rules: Rules, index: number, ability: Ability): BarSlotSpec {
  const first = ability.costs[0];
  const basic = blowOf(rules, rules.startingWeapon, "basic");
  return { index, key: ABILITY_KEYS[index] ?? "", ability: rules.tables.abilities.indexOf(ability) + 1, name: ability.name, glyph: ABILITY_GLYPHS[ability.id] ?? null, cost: first === undefined ? null : { resource: first.resource, amount: first.amount }, costs: ability.costs, basic: ability === basic, item: null, icon: ability.icon, count: null, spent: false };
}

/**
 * Whether an effect reads as a debuff, which the row draws with the danger ring and the corner fold (the design's
 * `StatusIcon`): it controls its bearer, or any of its modifiers harms it (less damage dealt or maximum health, more
 * damage taken, longer cooldowns). Anything else is a buff.
 *
 * @param effect The effect.
 * @returns "debuff" or "buff".
 * @example
 * effectTone(rules.effects.get("stun"));
 * // "debuff"
 */
export function effectTone(effect: Pick<Effect, "control" | "modifiers"> & { readonly periodic?: Effect["periodic"] }): "buff" | "debuff" {
  const m = effect.modifiers;
  return effect.control !== null || (effect.periodic ?? null) !== null || m.damageDealt < 0 || m.damageTaken > 0 || m.maxHealth < 0 || m.cooldown > 0 || (m.moveSpeed ?? 0) < 0 ? "debuff" : "buff";
}

/**
 * What a slot can do now: `empty` holds nothing; `spent` is an item slot at 0, greyed, whose press sends nothing
 * (ADR-121 decision 1.2); `locked` while the player is dead or its effects forbid acting (the
 * design's stunned state); `no-resource` while the last `vitals` show a cost's resource clearly short of it, short even
 * with the regeneration the window may bring (`clearlyShort`, ADR-073's amendment of 2026-10-02, item 6), as the fight
 * withholds a press; else
 * `ready`. A cooldown is drawn over any of these by the ticker, not named here.
 *
 * The basic blow's slot is the one exception (ADR-117 decision 9): it is `no-resource` while the last `vitals` show a
 * resource below its cost, with no slack, and it is still pressed and sent, since the map server swings a basic blow
 * that cannot be paid, weaker. There the state marks a blow that goes out weak, not one that cannot fire.
 */
export type SlotUsable = "ready" | "spent" | "locked" | "no-resource" | "empty";

/**
 * What the server last said of the player that decides a slot's use: dead, the effects' limits, each resource, and the
 * regeneration each may gain before the server judges an act sent now (`regenerationSlack` of its maximum).
 */
export interface PlayerState {
  readonly dead: boolean;
  readonly limits: number;
  readonly pools: Readonly<Record<ResourceId, number>>;
  readonly slack: Readonly<Record<ResourceId, number>>;
}

/**
 * Whether a slot holds anything: an ability or an item.
 *
 * @param slot The slot.
 * @returns True when the slot is not empty.
 */
export function slotFilled(slot: BarSlotSpec): boolean {
  return slot.ability !== 0 || slot.item !== null;
}

/**
 * What a slot can do now. A slot's costs are checked with the regeneration the window may bring, the basic blow's with
 * none: its state says the blow would go out weak now, which the next `vitals` corrects, and it withholds nothing.
 *
 * @param slot The slot.
 * @param player What the server last said of the player.
 * @returns The slot's state.
 */
export function slotUsable(slot: BarSlotSpec, player: PlayerState): SlotUsable {
  if (!slotFilled(slot)) {
    return "empty";
  }
  if (slot.spent) {
    return "spent";
  }
  if (player.dead || (player.limits & EffectLimit.Act) !== 0) {
    return "locked";
  }
  for (const cost of slot.costs) {
    if (player.pools[cost.resource] + (slot.basic ? 0 : player.slack[cost.resource]) < cost.amount) {
      return "no-resource";
    }
  }
  return "ready";
}
